import PublicLayout from "@/components/PublicLayout";
import { Link } from "react-router-dom";

function Policy({ title, sub, children }) {
  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="eyebrow">{sub}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2 text-stone-900 dark:text-stone-100">{title}</h1>
        <div className="mt-8 border-t border-border pt-8 text-stone-700 dark:text-stone-300 space-y-6 text-[15px] leading-relaxed">
          {children}
        </div>
      </div>
    </PublicLayout>
  );
}

export function Help() {
  return (
    <Policy title="Help & Customer Support" sub="Support & Assistance">
      <section className="space-y-3">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">Customer Support</h2>
        <p>
          We are here to assist you with event ticket registrations, payment confirmations, booking inquiries, and invoice requests.
        </p>
        <div className="bg-stone-50 dark:bg-stone-900/50 border border-border p-5 rounded-lg space-y-2 mt-4">
          <p><b>Legal Entity Name:</b> Common Ground Collective India</p>
          <p><b>Official Email:</b> <a href="mailto:office@cgcollective.in" className="text-[color:var(--brand-terracotta)] font-semibold">office@cgcollective.in</a></p>
          <p><b>Operating Location:</b> Visakhapatnam, Andhra Pradesh, India - 530003</p>
          <p><b>Support Hours:</b> Monday to Saturday, 9:00 AM – 6:00 PM IST</p>
          <p><b>Response Time:</b> Within 24 business hours</p>
        </div>
      </section>

      <section className="space-y-3 pt-6 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">Frequently Asked Questions</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-stone-900 dark:text-stone-100">1. How do I access my tickets?</h3>
            <p>Your tickets with unique QR codes are delivered immediately to your registered email upon payment. You can also view and download them anytime from our <Link to="/portal" className="text-[color:var(--brand-terracotta)] underline">Attendee Portal</Link>.</p>
          </div>
          <div>
            <h3 className="font-semibold text-stone-900 dark:text-stone-100">2. I made a payment but didn't receive an email.</h3>
            <p>Please check your email spam/promotions folder. If you still cannot locate it, please email us at <b>office@cgcollective.in</b> with your registered email and payment ID, and our team will re-issue your ticket immediately.</p>
          </div>
          <div>
            <h3 className="font-semibold text-stone-900 dark:text-stone-100">3. Can I get a GST invoice?</h3>
            <p>Yes. Check the "I need a GST invoice" option during checkout and provide your company name and GSTIN. An official invoice will be generated and stored with your booking.</p>
          </div>
        </div>
      </section>
    </Policy>
  );
}

export function Terms() {
  return (
    <Policy title="Terms and Conditions" sub="Legal Agreement">
      <p className="text-xs text-stone-500">Last updated: September 2026</p>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">1. Introduction & Acceptance</h2>
        <p>
          These Terms and Conditions constitute a legally binding agreement between you (the "Customer", "User", or "Attendee") and <b>Common Ground Collective India</b> ("we", "us", or "our"), governing your access to and use of our website (<b>register.cgcollective.in</b> and <b>cgcollective.in</b>) and our event registration and ticketing services.
        </p>
        <p>
          By booking tickets, registering for events, or making payments on this platform, you acknowledge that you have read, understood, and agree to be bound by these Terms and our associated policies.
        </p>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">2. Services Offered</h2>
        <p>
          Common Ground Collective India organizes, curates, and facilitates registrations for community gatherings, sustainability workshops, artisanal pop-up markets, fundraisers, and cultural events. Our platform facilitates ticket reservations, online payment processing via authorized payment gateways, digital ticket distribution, and attendee management.
        </p>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">3. Booking & Payment Terms</h2>
        <ul className="list-disc list-inside space-y-2">
          <li>All ticket prices are listed in Indian Rupees (INR / ₹) and are inclusive or exclusive of applicable taxes and convenience/checkout charges as stated during checkout.</li>
          <li>Payments must be made in full through authorized payment methods (UPI, Cards, Netbanking) facilitated securely via <b>Razorpay Payment Gateway</b>.</li>
          <li>A temporary ticket reservation is held for up to 15 minutes during the checkout process. If payment is not completed within this timeframe, the inventory is released.</li>
          <li>Tickets are confirmed only upon successful debit authorization and payment capture verified by our payment gateway.</li>
        </ul>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">4. Admission & Event Rules</h2>
        <ul className="list-disc list-inside space-y-2">
          <li>Each ticket contains a unique QR code valid for a single entry at the venue entrance. Duplication or tampering with QR codes will result in refusal of entry.</li>
          <li>Attendees agree to abide by the venue's rules, safety guidelines, and Common Ground Collective's code of conduct. We reserve the right to deny admission without refund in cases of disruptive, unlawful, or unsafe behavior.</li>
          <li>Certain events (e.g., workshops with equipment, evening galas) may have age restrictions as stated on the event description page.</li>
        </ul>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">5. Limitation of Liability & Governing Law</h2>
        <p>
          In no event shall Common Ground Collective India, its directors, or affiliates be liable for any indirect, consequential, or incidental damages arising out of event attendance or platform usage. Our total liability for any claim shall not exceed the amount paid by you for the ticket in dispute.
        </p>
        <p>
          These terms shall be governed by and construed in accordance with the laws of India. Any disputes arising in connection with these terms shall be subject to the exclusive jurisdiction of the competent courts in <b>Visakhapatnam, Andhra Pradesh, India</b>.
        </p>
      </section>
    </Policy>
  );
}

export function Privacy() {
  return (
    <Policy title="Privacy Policy" sub="Data Protection & Security">
      <p className="text-xs text-stone-500">Last updated: September 2026</p>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">1. Commitment to Privacy</h2>
        <p>
          <b>Common Ground Collective India</b> is committed to protecting the privacy and personal information of our event attendees and website visitors. This Privacy Policy outlines what information we collect, how we use it, and how your data is safeguarded.
        </p>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">2. Information We Collect</h2>
        <p>When you register for an event, purchase tickets, or use our attendee portal, we collect:</p>
        <ul className="list-disc list-inside space-y-2">
          <li><b>Contact Details:</b> Name, email address, and mobile phone number for ticket delivery and event updates.</li>
          <li><b>Attendee Details:</b> Names and optional responses to event-specific questions (e.g. dietary choices, workshop skill levels).</li>
          <li><b>Billing Information:</b> Name, company name, address, and GSTIN if you request a tax invoice.</li>
        </ul>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">3. Payment Security (Razorpay)</h2>
        <div className="bg-stone-50 dark:bg-stone-900/50 border border-border p-4 rounded-lg space-y-2">
          <p className="font-semibold text-stone-900 dark:text-stone-100">Payment Information is Never Stored on Our Servers:</p>
          <p>
            All electronic payment transactions are processed through <b>Razorpay Payment Gateway</b>, which is certified under <b>PCI-DSS (Payment Card Industry Data Security Standard) Level 1</b>. We do not collect, process, or store your credit card numbers, debit card details, CVVs, netbanking credentials, or UPI PINs.
          </p>
        </div>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">4. How We Use Your Information</h2>
        <ul className="list-disc list-inside space-y-2">
          <li>To generate and deliver your electronic tickets, QR codes, and booking receipts.</li>
          <li>To notify you about important event updates, venue directions, or schedule changes.</li>
          <li>To verify check-in credentials at the venue entrance.</li>
          <li>We <b>never sell, rent, or trade</b> your personal information to third parties.</li>
        </ul>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">5. Grievance Officer & Contact</h2>
        <p>
          If you have any questions or grievances regarding our privacy practices, please contact our Privacy Coordinator at:
        </p>
        <p>
          <b>Email:</b> <a href="mailto:office@cgcollective.in" className="text-[color:var(--brand-terracotta)] font-semibold">office@cgcollective.in</a><br/>
          <b>Address:</b> Common Ground Collective India, Visakhapatnam, Andhra Pradesh, India - 530003
        </p>
      </section>
    </Policy>
  );
}

export function Refund() {
  return (
    <Policy title="Cancellation and Refund Policy" sub="Refund Guidelines">
      <p className="text-xs text-stone-500">Last updated: September 2026</p>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">1. Cancellation by Attendee</h2>
        <ul className="list-disc list-inside space-y-2">
          <li>
            <b>Standard Events & Workshops:</b> Attendees may request a cancellation up to <b>7 calendar days</b> prior to the scheduled event start date for a full refund (minus any standard payment gateway processing fee).
          </li>
          <li>
            <b>Within 7 Days of Event:</b> Because venues, catering, and materials are committed in advance, cancellation requests made less than 7 days before the event are non-refundable. However, tickets may be transferred to another individual with prior written notification.
          </li>
          <li>
            <b>Fundraisers:</b> Contributions to charitable and environmental fundraisers (e.g. Eco-Fundraiser Galas) are non-refundable due to charitable disbursement guidelines, but passes may be transferred.
          </li>
        </ul>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">2. Cancellation or Rescheduling by Organizer</h2>
        <p>
          If an event is cancelled due to unforeseen circumstances, weather, or operational constraints, registered attendees will be entitled to a <b>100% full refund</b>. If an event is rescheduled to a new date and an attendee is unable to attend, a full refund will be provided upon request.
        </p>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">3. Refund Processing Timeline</h2>
        <div className="bg-stone-50 dark:bg-stone-900/50 border border-border p-4 rounded-lg space-y-2">
          <p>
            Approved refunds are credited directly to the <b>original payment source</b> (bank account, credit/debit card, or UPI) through Razorpay.
          </p>
          <p>
            <b>Timeline:</b> Refunds typically reflect in your account within <b>5 to 7 business days</b> from the date of approval, subject to your issuing bank's clearing cycle.
          </p>
        </div>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">4. How to Request a Refund</h2>
        <p>
          To initiate a refund, please send an email to <b>office@cgcollective.in</b> with:
        </p>
        <ul className="list-disc list-inside space-y-1">
          <li>Your Booking Reference (e.g., <code>CGC-XXXXXX</code>)</li>
          <li>Registered Purchaser Name and Email</li>
          <li>Reason for cancellation</li>
        </ul>
        <p className="mt-2 text-stone-600 dark:text-stone-400 text-sm">
          Our team will acknowledge and process eligible requests within 2 business days.
        </p>
      </section>
    </Policy>
  );
}

export function Shipping() {
  return (
    <Policy title="Shipping & Delivery Policy" sub="Digital Fulfillment">
      <p className="text-xs text-stone-500">Last updated: September 2026</p>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">1. Digital Delivery of Tickets</h2>
        <p>
          <b>Common Ground Collective India</b> provides digital ticketing and registration services for in-person and online events. We do not ship physical merchandise or paper tickets via courier.
        </p>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">2. Delivery Method & Timelines</h2>
        <div className="space-y-3">
          <div className="bg-stone-50 dark:bg-stone-900/50 border border-border p-4 rounded-lg space-y-2">
            <p><b>Delivery Mode:</b> Electronic Mail (Email) & Online Attendee Portal</p>
            <p><b>Delivery Timeline:</b> <b>Immediate (Within 1 to 5 minutes)</b> following successful payment confirmation.</p>
            <p><b>Delivery Cost:</b> ₹0 (Free electronic delivery for all tickets)</p>
          </div>
          <p>
            Upon successful transaction completion via Razorpay:
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li>You will immediately see your confirmed booking page with your unique booking reference and digital QR code.</li>
            <li>A confirmation email containing booking details, venue location, and ticket link is automatically dispatched to the purchaser's email address.</li>
            <li>Digital tickets can also be accessed, viewed, and downloaded at any time via the <Link to="/portal" className="text-[color:var(--brand-terracotta)] font-semibold underline">Attendee Portal</Link> using OTP verification.</li>
          </ul>
        </div>
      </section>

      <section className="space-y-3 pt-4 border-t border-border">
        <h2 className="text-xl font-bold text-stone-900 dark:text-stone-100">3. Non-Receipt of Digital Delivery</h2>
        <p>
          If you have completed your payment and have not received your confirmation email within 15 minutes, please:
        </p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Check your email Spam or Promotions folders.</li>
          <li>Log into the <Link to="/portal" className="text-[color:var(--brand-terracotta)] underline">Attendee Portal</Link> using your email to retrieve your tickets.</li>
          <li>Contact our support team at <b>office@cgcollective.in</b> with your payment details, and we will manually verify and resend your pass immediately.</li>
        </ol>
      </section>
    </Policy>
  );
}

export function Contact() {
  return (
    <Policy title="Contact Us" sub="Get in Touch">
      <p>
        Have questions about upcoming events, community partnerships, or need assistance with your booking? Reach out to us through any of the channels below.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className="bg-stone-50 dark:bg-stone-900/50 border border-border p-6 rounded-lg space-y-3">
          <h3 className="font-semibold text-lg text-stone-900 dark:text-stone-100">Official Correspondence</h3>
          <p className="text-sm">
            <b>Entity Name:</b> Common Ground Collective India<br/>
            <b>Operating Address:</b> Visakhapatnam, Andhra Pradesh, India - 530003
          </p>
          <p className="text-sm">
            <b>General Inquiries:</b> <a href="mailto:office@cgcollective.in" className="text-[color:var(--brand-terracotta)] font-medium">office@cgcollective.in</a><br/>
            <b>Support & Ticketing:</b> <a href="mailto:hello@cgcollective.in" className="text-[color:var(--brand-terracotta)] font-medium">hello@cgcollective.in</a>
          </p>
        </div>

        <div className="bg-stone-50 dark:bg-stone-900/50 border border-border p-6 rounded-lg space-y-3">
          <h3 className="font-semibold text-lg text-stone-900 dark:text-stone-100">Support Hours</h3>
          <p className="text-sm text-stone-600 dark:text-stone-400">
            Our operational desk is active:
          </p>
          <p className="text-sm">
            <b>Monday – Saturday:</b> 9:00 AM to 6:00 PM IST<br/>
            <b>Sunday:</b> Closed (Emergency event day support active via venue coordinators)
          </p>
          <p className="text-sm text-stone-500">
            We aim to resolve all email queries within 24 business hours.
          </p>
        </div>
      </div>
    </Policy>
  );
}
